import React, { useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function BulkOperationsPage({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('import');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [operationType, setOperationType] = useState('employees');

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
    setUploadResult(null);
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Please select a file');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      if (operationType === 'employees') {
        endpoint = `${API}/api/bulk-operations/employees/import-csv`;
      } else if (operationType === 'attendance') {
        endpoint = `${API}/api/bulk-operations/attendance/import-csv`;
      }

      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResult(response.data);
      alert(`Import completed! Imported: ${response.data.imported}, Skipped: ${response.data.skipped}`);
      setSelectedFile(null);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      if (type === 'employees') {
        endpoint = `${API}/api/bulk-operations/employees/export-csv`;
      } else if (type === 'attendance') {
        const month = new Date().getMonth() + 1;
        const year = new Date().getFullYear();
        endpoint = `${API}/api/bulk-operations/attendance/export-csv?month=${month}&year=${year}`;
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      alert('Export successful!');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = (type) => {
    let csvContent = '';
    
    if (type === 'employees') {
      csvContent = 'employee_id,first_name,last_name,official_email,mobile_number,date_of_joining,designation,departments,employment_type,basic_salary\n';
      csvContent += 'EMP001,John,Doe,john@example.com,9876543210,2024-01-15,Manager,"HR,Admin",Full-time,50000\n';
    } else if (type === 'attendance') {
      csvContent = 'employee_id,date,status,check_in_time,check_out_time\n';
      csvContent += 'EMP001,2024-12-20,Present,09:00:00,18:00:00\n';
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${type}_template.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Bulk Operations</h1>
          <p className="mt-2 text-gray-600">Import/Export employees and attendance data in bulk</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('import')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'import'
                ? 'bg-[#2b3380] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📥 Import Data
          </button>
          <button
            onClick={() => setActiveTab('export')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'export'
                ? 'bg-[#2b3380] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📤 Export Data
          </button>
        </div>

        {/* Import Tab */}
        {activeTab === 'import' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Import Data from CSV</h2>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Data Type</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setOperationType('employees')}
                  className={`px-6 py-3 rounded-lg border-2 transition ${
                    operationType === 'employees'
                      ? 'border-[#2b3380] bg-[#2b3380] text-white'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  👥 Employees
                </button>
                <button
                  onClick={() => setOperationType('attendance')}
                  className={`px-6 py-3 rounded-lg border-2 transition ${
                    operationType === 'attendance'
                      ? 'border-[#2b3380] bg-[#2b3380] text-white'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  📋 Attendance
                </button>
              </div>
            </div>

            <div className="mb-6">
              <button
                onClick={() => downloadTemplate(operationType)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                💾 Download CSV Template
              </button>
              <p className="text-sm text-gray-500 mt-2">Download a sample template to see the required format</p>
            </div>

            <form onSubmit={handleImport} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                />
                {selectedFile && (
                  <p className="text-sm text-green-600 mt-2">✅ Selected: {selectedFile.name}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !selectedFile}
                className="px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:bg-gray-400"
              >
                {loading ? 'Importing...' : 'Import Data'}
              </button>
            </form>

            {uploadResult && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Import Results</h3>
                <div className="space-y-1 text-sm">
                  <p>✅ Imported: <span className="font-semibold">{uploadResult.imported}</span></p>
                  <p>⏭️ Skipped: <span className="font-semibold">{uploadResult.skipped}</span></p>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-3">
                      <p className="text-red-600 font-semibold">Errors:</p>
                      <ul className="list-disc list-inside text-red-600">
                        {uploadResult.errors.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes</h3>
              <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
                <li>Make sure your CSV follows the template format exactly</li>
                <li>Existing records with same email/employee_id will be skipped</li>
                <li>For best results, import data in batches of 100-500 records</li>
                <li>Date format should be YYYY-MM-DD</li>
              </ul>
            </div>
          </div>
        )}

        {/* Export Tab */}
        {activeTab === 'export' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-6">Export Data to CSV</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Employees Export */}
              <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-[#2b3380] transition">
                <div className="flex items-center mb-4">
                  <span className="text-4xl mr-4">👥</span>
                  <div>
                    <h3 className="text-lg font-semibold">Export Employees</h3>
                    <p className="text-sm text-gray-600">All employee records</p>
                  </div>
                </div>
                <button
                  onClick={() => handleExport('employees')}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:bg-gray-400"
                >
                  {loading ? 'Exporting...' : 'Export Employees'}
                </button>
              </div>

              {/* Attendance Export */}
              <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-[#2b3380] transition">
                <div className="flex items-center mb-4">
                  <span className="text-4xl mr-4">📋</span>
                  <div>
                    <h3 className="text-lg font-semibold">Export Attendance</h3>
                    <p className="text-sm text-gray-600">Current month records</p>
                  </div>
                </div>
                <button
                  onClick={() => handleExport('attendance')}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:bg-gray-400"
                >
                  {loading ? 'Exporting...' : 'Export Attendance'}
                </button>
              </div>
            </div>

            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-900 mb-2">✅ Export Features</h3>
              <ul className="list-disc list-inside text-sm text-green-800 space-y-1">
                <li>Download complete data in CSV format</li>
                <li>Ready to open in Excel, Google Sheets, or any spreadsheet software</li>
                <li>Use exported data for backup, analysis, or migration</li>
                <li>Re-import after modifications using the Import tab</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default BulkOperationsPage;